import { useEffect, useState, type FormEvent } from 'react';
import { useParams } from '@tanstack/react-router';
import { formatMoney, money, parseMoneyInput } from '@buildmate/core';
import { Button, Field, TextArea } from '@buildmate/ui-web';
import { usePorts } from '../lib/ports-context.tsx';

/**
 * Send a price without an account (SCRUM-308).
 *
 * Outside every guard, because the person reading it has no account —
 * that is the entire point. The link carries one package and this page
 * shows one package: no job, no other trades, no other prices. A
 * bearer token is whoever holds it, so the page is as narrow as the
 * token.
 */
type State =
  | { phase: 'loading' }
  | { phase: 'failed'; message: string }
  | {
      phase: 'ready';
      title: string;
      scope: string;
      dueDate?: string;
      companyName: string;
      open: boolean;
    }
  | { phase: 'sent'; companyName: string };

export function PriceBid() {
  const { token } = useParams({ from: '/price/$token' });
  const ports = usePorts();
  const [state, setState] = useState<State>({ phase: 'loading' });
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    ports.functions
      .call('bidPackageForToken', { token })
      .then((found) => {
        if (!live) return;
        setState({
          phase: 'ready',
          title: found.title,
          scope: found.scope,
          ...(found.dueDate ? { dueDate: found.dueDate } : {}),
          companyName: found.companyName,
          open: found.open,
        });
        // Their own earlier price, so correcting it is editing rather
        // than remembering what they said.
        if (found.myAmountMinor !== null) {
          setAmount((found.myAmountMinor / 100).toFixed(2));
          setNotes(found.myNotes);
        }
      })
      .catch((cause: unknown) => {
        if (!live) return;
        setState({
          phase: 'failed',
          message: cause instanceof Error ? cause.message : 'That link did not work.',
        });
      });
    return () => {
      live = false;
    };
  }, [ports, token]);

  async function send(event: FormEvent) {
    event.preventDefault();
    if (state.phase !== 'ready') return;
    const parsed = parseMoneyInput(amount);
    if (!parsed || parsed.amount <= 0) {
      setProblem('A price is needed before this can be sent.');
      return;
    }
    setBusy(true);
    setProblem(null);
    try {
      await ports.functions.call('priceBidByToken', {
        token,
        amountMinor: parsed.amount,
        notes,
      });
      setState({ phase: 'sent', companyName: state.companyName });
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : 'That price did not send.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="mx-auto max-w-xl px-4 py-10">
      {state.phase === 'loading' && <p className="text-sm text-slate-500">Loading…</p>}

      {state.phase === 'failed' && (
        <>
          <h1 className="text-lg font-semibold text-slate-900">That link did not work</h1>
          <p role="alert" className="mt-1 text-sm text-slate-600">
            {state.message} Ask whoever sent it for a new one.
          </p>
        </>
      )}

      {state.phase === 'sent' && (
        <>
          <h1 className="text-lg font-semibold text-slate-900">Price sent</h1>
          <p className="mt-1 text-sm text-slate-600">
            {state.companyName} has it. You can close this page — and the link still works if you
            need to change the number.
          </p>
        </>
      )}

      {state.phase === 'ready' && (
        <>
          <p className="text-sm text-slate-600">{state.companyName} would like a price for</p>
          <h1 className="text-xl font-semibold text-slate-900">{state.title}</h1>
          {state.dueDate && (
            <p className="mt-1 text-sm text-slate-600">Prices wanted by {state.dueDate}</p>
          )}
          {state.scope && (
            <p className="mt-4 max-w-prose whitespace-pre-line text-sm text-slate-700">
              {state.scope}
            </p>
          )}

          {state.open ? (
            <form onSubmit={send} className="mt-6 flex flex-col gap-4">
              <Field
                label="Your price"
                hint="The whole package, in dollars."
                inputMode="decimal"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />
              <TextArea
                label="Anything they should know"
                hint="Exclusions, lead times, what the price assumes."
                rows={4}
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
              />
              {problem && (
                <p role="alert" className="text-sm text-red-700">
                  {problem}
                </p>
              )}
              <div>
                <Button type="submit" loading={busy}>
                  Send my price
                </Button>
              </div>
              <p className="text-xs text-slate-500">
                {amount.trim() !== '' && parseMoneyInput(amount)
                  ? `That is ${formatMoney(parseMoneyInput(amount) ?? money(0))}.`
                  : 'No account needed. This link prices this one package.'}
              </p>
            </form>
          ) : (
            <p className="mt-6 rounded-md bg-slate-100 p-3 text-sm text-slate-700">
              This package is no longer taking prices. If you think that is wrong, ring whoever
              asked you.
            </p>
          )}
        </>
      )}
    </main>
  );
}
